import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Prefix a relative "/uploads/..." path (as stored in the DB) with the
// backend's origin, so <img src> resolves against api.docapps.in instead of
// the frontend's own domain.
export const getFileUrl = (url) => {
  if (!url || url.startsWith('http')) return url;
  return `${BASE_URL.replace(/\/api\/?$/, '')}${url}`;
};

// Shared 5 MB attachment cap for every upload UI (prescriptions, lab tests, billing,
// EMR, pharmacy, messages) — mirrored server-side in backend/middleware/upload.js.
export const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;
export const MAX_ATTACHMENT_SIZE_MESSAGE = 'File size must not exceed 5 MB.';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  error => Promise.reject(error)
);

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const stored = localStorage.getItem('user');
      const role = stored ? (JSON.parse(stored).role || 'Patient').toLowerCase() : 'patient';
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = `/${role}/login`;
    }
    return Promise.reject(error?.response?.data || error);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authLogin    = (data) => api.post('/auth/login', data);
export const authRegister = (data) => api.post('/auth/signup', data);
export const getMe        = ()     => api.get('/auth/me');
export const updateProfile  = (data) => api.put('/auth/profile', data);
export const changePassword = (data) => api.put('/auth/change-password', data);
export const uploadProfileImage = (formData) => api.post('/auth/profile-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// ── Dashboard ─────────────────────────────────────────────────────────────────
export const getDashboardData = () => api.get('/dashboard');

// ── Doctors dropdown (all authenticated roles can access) ─────────────────────
export const getDoctors = (params) => api.get('/auth/doctors', { params });

// ── Registered Patients dropdown (only patients who have user accounts) ───────
export const getRegisteredPatients = (params) => api.get('/auth/registered-patients', { params });

// ── Appointments ──────────────────────────────────────────────────────────────
export const getAppointments   = (params) => api.get('/appointments', { params });
export const getAppointment    = (id)     => api.get(`/appointments/${id}`);
export const createAppointment = (data)   => api.post('/appointments', data);
export const updateAppointment = (id, data) => api.put(`/appointments/${id}`, data);
export const deleteAppointment = (id)     => api.delete(`/appointments/${id}`);

// ── Patients ──────────────────────────────────────────────────────────────────
export const getPatients   = (params) => api.get('/patients', { params });
export const getPatient    = (id)     => api.get(`/patients/${id}`);
export const createPatient = (data)   => api.post('/patients', data);
export const updatePatient = (id, data) => api.put(`/patients/${id}`, data);
export const deletePatient = (id)     => api.delete(`/patients/${id}`);

// ── Prescriptions ─────────────────────────────────────────────────────────────
const multipartCfg = { headers: { 'Content-Type': 'multipart/form-data' } };
export const getPrescriptions   = (params) => api.get('/prescriptions', { params });
export const getPrescription    = (id)     => api.get(`/prescriptions/${id}`);
export const createPrescription = (data)   => api.post('/prescriptions', data, data instanceof FormData ? multipartCfg : {});
export const updatePrescription = (id, data) => api.put(`/prescriptions/${id}`, data, data instanceof FormData ? multipartCfg : {});
export const deletePrescription = (id)     => api.delete(`/prescriptions/${id}`);

// ── Lab Tests ─────────────────────────────────────────────────────────────────
export const getLabTests   = (params) => api.get('/labtests', { params });
export const getLabTest    = (id)     => api.get(`/labtests/${id}`);
export const createLabTest = (data)   => api.post('/labtests', data, data instanceof FormData ? multipartCfg : {});
export const updateLabTest = (id, data) => api.put(`/labtests/${id}`, data, data instanceof FormData ? multipartCfg : {});
export const deleteLabTest = (id)     => api.delete(`/labtests/${id}`);

// ── Billing ───────────────────────────────────────────────────────────────────
export const getBillings   = (params) => api.get('/billing', { params });
export const getBilling    = (id)     => api.get(`/billing/${id}`);
export const createBilling = (data)   => api.post('/billing', data, data instanceof FormData ? multipartCfg : {});
export const updateBilling = (id, data) => api.put(`/billing/${id}`, data, data instanceof FormData ? multipartCfg : {});
export const deleteBilling = (id)     => api.delete(`/billing/${id}`);
export const createBillingOrder    = (id)         => api.post(`/billing/${id}/pay/order`);
export const verifyBillingPayment  = (id, data)   => api.post(`/billing/${id}/pay/verify`, data);
export const getPendingApprovalBillings = ()      => api.get('/billing/pending-approval');
export const approveBilling        = (id)         => api.put(`/billing/${id}/approve`);
export const rejectBilling         = (id, reason) => api.put(`/billing/${id}/reject`, { reason });

// ── Messages & Chat Requests ──────────────────────────────────────────────────
export const getMessages          = (params) => api.get('/messages', { params });
export const sendMessage          = (data)   => api.post('/messages', data);
export const sendMessageWithFile  = (formData) => api.post('/messages', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const markMessageRead      = (id)     => api.put(`/messages/${id}/read`);
export const getChatRequests      = ()       => api.get('/messages/chat-requests');
export const getConversations     = ()       => api.get('/messages/conversations');
export const deleteConversation   = (id)     => api.delete(`/messages/conversations/${id}`);
export const createChatRequest    = (data)   => api.post('/messages/chat-requests', data);
export const updateChatRequest    = (id, data) => api.put(`/messages/chat-requests/${id}`, data);
export const blockConversation    = (id, action)     => api.put(`/messages/chat-requests/${id}/block`, { action });
export const disableConversation  = (id, disabled)   => api.put(`/messages/chat-requests/${id}/disable`, { disabled });
export const unfollowConversation = (id, unfollowed) => api.put(`/messages/chat-requests/${id}/unfollow`, { unfollowed });
export const deleteMessage        = (id)     => api.delete(`/messages/${id}`);
export const sendTypingStatus     = (data)   => api.post('/messages/typing', data);
export const getTypingStatus      = (id)     => api.get(`/messages/typing/${id}`);

// ── Telemedicine ──────────────────────────────────────────────────────────────
export const getTelemedicineSessions  = (params) => api.get('/telemedicine', { params });
export const getTelemedicineSession   = (id)     => api.get(`/telemedicine/${id}`);
export const createTelemedicineSession = (data)  => api.post('/telemedicine', data);
export const updateTelemedicineSession = (id, data) => api.put(`/telemedicine/${id}`, data);
export const deleteTelemedicineSession = (id)    => api.delete(`/telemedicine/${id}`);
export const getTelemedicineStats     = ()       => api.get('/telemedicine/stats');
export const uploadTelemedicineRecording = (id, formData) =>
  api.post(`/telemedicine/${id}/recording`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getTelemedicineJwt = (id) => api.get(`/telemedicine/jwt/${id}`);
// Recording is behind auth + ownership checks now (no public static URL) —
// fetch it as an authenticated blob, then the page turns that into an object URL.
export const getTelemedicineRecordingBlob = (id, file) => api.get(`/telemedicine/${id}/recording`, { responseType: 'blob', params: file ? { file } : undefined });

// ── EMR ───────────────────────────────────────────────────────────────────────
export const getEMRRecords   = (params) => api.get('/emr', { params });
export const getEMRRecord    = (id)     => api.get(`/emr/${id}`);
export const createEMRRecord = (data)   => api.post('/emr', data, data instanceof FormData ? multipartCfg : {});
export const updateEMRRecord = (id, data) => api.put(`/emr/${id}`, data, data instanceof FormData ? multipartCfg : {});
export const deleteEMRRecord = (id)     => api.delete(`/emr/${id}`);

// ── Pharmacy ──────────────────────────────────────────────────────────────────
export const getMedicines   = (params) => api.get('/pharmacy', { params });
export const getMedicine    = (id)     => api.get(`/pharmacy/${id}`);
export const createMedicine = (data)   => api.post('/pharmacy', data, data instanceof FormData ? multipartCfg : {});
export const updateMedicine = (id, data) => api.put(`/pharmacy/${id}`, data, data instanceof FormData ? multipartCfg : {});
export const deleteMedicine = (id)     => api.delete(`/pharmacy/${id}`);

// ── Notifications ─────────────────────────────────────────────────────────────
export const getNotifications       = ()   => api.get('/notifications');
export const markNotificationRead   = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const deleteNotification     = (id) => api.delete(`/notifications/${id}`);

// ── Payments (Razorpay) ───────────────────────────────────────────────────────
export const createPaymentOrder  = (data)             => api.post('/payments/create-order', data);
export const verifyPayment       = (data)             => api.post('/payments/verify', data);
export const getPaymentStatus    = (orderId)          => api.get(`/payments/status/${orderId}`);
export const getConsultFee            = ()       => api.get('/payments/fee');
export const getDoctorPaymentSummary  = ()       => api.get('/payments/doctor-summary');
export const getSlotAvailability = (doctorId, date)   => api.get('/appointments/slots', { params: { doctorId, date } });

// ── Admin ─────────────────────────────────────────────────────────────────────
export const adminGetUsers           = (params)  => api.get('/admin/users', { params });
export const adminGetUser            = (id)      => api.get(`/admin/users/${id}`);
export const adminUpdateUser         = (id, data) => api.put(`/admin/users/${id}`, data);
export const adminDeleteUser         = (id)      => api.delete(`/admin/users/${id}`);
export const adminImpersonate        = (userId)  => api.post(`/admin/impersonate/${userId}`);
export const adminGetAnalytics       = ()        => api.get('/admin/analytics');
export const adminGetDoctorAnalytics  = (doctorId) => api.get(`/admin/doctor-analytics/${doctorId}`);
export const adminGetPaymentSummary   = (params)   => api.get('/admin/payment-summary', { params });
export const adminGetChats              = ()        => api.get('/admin/chats');
export const adminGetConversations      = ()        => api.get('/admin/conversations');
export const adminGetDoctorPatientMap   = ()        => api.get('/admin/doctor-patient-map');
export const adminAssignDoctor          = (data)    => api.put('/admin/assign-doctor', data);

// ── Doctor Holidays (weekend rule is automatic/backend-only; these are the
//    admin-managed one-time/annual holiday records) ────────────────────────────
export const getDoctorHolidays    = (doctorId) => api.get('/doctor-holidays', { params: { doctorId } });
export const createDoctorHoliday  = (data)     => api.post('/doctor-holidays', data);
export const updateDoctorHoliday  = (id, data) => api.put(`/doctor-holidays/${id}`, data);
export const deleteDoctorHoliday  = (id)       => api.delete(`/doctor-holidays/${id}`);

// ── Banners (slider) ──────────────────────────────────────────────────────────
export const getBanners  = (params) => api.get('/banners', { params });
export const createBanner = (formData) => api.post('/banners', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteBanner = (id) => api.delete(`/banners/${id}`);

// ── Help & Support (FAQs, common problems, contact info) ──────────────────────
export const getSupportContent = (params) => api.get('/support', { params });
export const createSupportItem = (data) => api.post('/support/items', data);
export const updateSupportItem = (id, data) => api.put(`/support/items/${id}`, data);
export const deleteSupportItem = (id) => api.delete(`/support/items/${id}`);
export const createSupportContact = (data) => api.post('/support/contacts', data);
export const updateSupportContact = (id, data) => api.put(`/support/contacts/${id}`, data);
export const deleteSupportContact = (id) => api.delete(`/support/contacts/${id}`);

export default api;
